// CLOSED-LOOPS (B3): commitments — every request in any direction (a person asking a DE, a DE
// asking a person, DE to DE, a DE's helper) as one record with a clock the system owns, and the
// decisions a failure or a blocker makes owed. A person's Inbox reads the ones asked of them and
// by them; one action route covers every move (accept, hand in, verify, fail with options, choose,
// forward, extend, withdraw). DEs use the matching MCP tools (commitment_*). Base plan
// (`feature: core`). Handlers: services/api/src/routes/commitments.ts; rules:
// services/api/src/domain/commitments.
import { z } from "zod";
import {
  BlockerCategory,
  CommitmentActorKind,
  CommitmentCause,
  CommitmentKind,
  CommitmentState,
  CommitmentVerify,
} from "../enums";
import { Timestamp, text, Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

export const CommitmentParty = z
  .strictObject({
    kind: CommitmentActorKind,
    membership_id: Uuid.describe("A helper party: the DE whose runtime runs it"),
    ref: z.string().nullable().describe("A helper's slug or profile; null otherwise"),
    name: z.string().describe('Who, in words ("Ana", "Content Lead\'s Art Director")'),
  })
  .meta({ id: "CommitmentParty" });

export const CommitmentOption = z
  .strictObject({
    key: z.string().max(1),
    label: z.string().max(160),
    detail: z.string().max(400).nullable(),
    action: z.enum([
      "retry",
      "retry_changed",
      "reassign",
      "extend",
      "accept_partial",
      "drop",
      "approve",
      "other",
    ]),
    recommended: z.boolean(),
  })
  .meta({ id: "CommitmentOption" });

export const CommitmentFailure = z
  .strictObject({
    cause: CommitmentCause,
    category: BlockerCategory.nullable(),
    what_happened: z.string(),
    tried: z.array(z.string()),
    partial: z.array(z.strictObject({ label: z.string(), ref: z.string().nullable() })),
    options: z.array(CommitmentOption),
    recommendation: z.string(),
    question: z.string(),
    decide_by: Timestamp,
    default_option: z.string().nullable(),
    logs: z.array(z.string()),
  })
  .meta({ id: "CommitmentFailure" });

export const CommitmentEvidence = z
  .strictObject({
    kind: z.enum(["message", "task", "attachment", "helper_card", "url", "note"]),
    ref: z.string().min(1).max(500),
    label: z.string().max(160).nullable().optional(),
  })
  .meta({ id: "CommitmentEvidence" });

export const CommitmentLogEntry = z
  .strictObject({
    at: Timestamp,
    action: z.string(),
    actor_kind: z.string(),
    actor_membership_id: Uuid.nullable(),
    from: z.string().nullable(),
    to: z.string().nullable(),
    rung: z.int().nullable(),
    told: z.array(z.string()).describe("Who heard of it (names)"),
    note: z.string().nullable(),
  })
  .meta({ id: "CommitmentLogEntry" });

/** What the caller may do with it now (the Inbox shows these as one-click actions). */
export const CommitmentMove = z.enum([
  "accept",
  "reject",
  "progress",
  "submit",
  "verify",
  "fail",
  "cancel",
  "choose",
  "forward",
  "extend",
]);

export const CommitmentView = z
  .strictObject({
    id: Uuid,
    kind: CommitmentKind,
    request_type: z.string(),
    state: CommitmentState,
    state_reason: z.string().nullable(),
    state_at: Timestamp,
    intent: z.string(),
    expected_outcome: z.string(),
    done_when: z.array(z.string()),
    requester: CommitmentParty,
    owner: CommitmentParty,
    accountable_membership_id: Uuid.nullable(),
    waiting_on: z.strictObject({ membership_id: Uuid, name: z.string() }).nullable(),
    waiting_since: Timestamp.nullable(),
    due_at: Timestamp,
    accept_by: Timestamp,
    verify_method: CommitmentVerify,
    verifier_membership_id: Uuid.nullable(),
    verify_by: Timestamp.nullable(),
    expires_at: Timestamp,
    parent_id: Uuid.nullable(),
    context_id: Uuid.nullable(),
    subject: z.strictObject({ kind: z.string(), id: Uuid }).nullable(),
    result: z.string().nullable(),
    evidence: z.array(CommitmentEvidence),
    feedback: z.string().nullable(),
    attempts: z.int(),
    max_attempts: z.int(),
    replans: z.int(),
    max_replans: z.int(),
    loops: z.int(),
    blocker_category: BlockerCategory.nullable(),
    blocker_detail: z.string().nullable(),
    cause: CommitmentCause.nullable(),
    failure: CommitmentFailure.nullable(),
    failure_text: z
      .string()
      .nullable()
      .describe("The failure (or the decision asked) in the escalation message's plain words"),
    options: z.array(CommitmentOption).nullable(),
    default_option: z.string().nullable(),
    chosen_option: z.string().nullable(),
    choice_note: z.string().nullable(),
    rung: z.int(),
    next_rung_at: Timestamp.nullable(),
    escalated_to_membership_id: Uuid.nullable(),
    my_roles: z.array(z.enum(["requester", "owner", "verifier", "decider", "escalated"])),
    moves: z.array(CommitmentMove),
    created_at: Timestamp,
    updated_at: Timestamp,
    log: z.array(CommitmentLogEntry).optional(),
  })
  .meta({ id: "CommitmentView" });

export const CommitmentList = z
  .object({ data: z.array(CommitmentView) })
  .meta({ id: "CommitmentList" });

export const CommitmentListQuery = z.object({
  side: z
    .enum(["of_me", "by_me", "all"])
    .optional()
    .describe("of_me: asked of you (or escalated to you); by_me: asked by you; all (default)"),
  open: z
    .enum(["true", "false"])
    .optional()
    .describe("true (default): live ones only; false: the last 50 closed"),
});

export const CommitmentOptionInput = z
  .strictObject({
    label: text(160),
    detail: text(400).nullable().optional(),
    action: CommitmentOption.shape.action.optional(),
    recommended: z.boolean().optional(),
  })
  .meta({ id: "CommitmentOptionInput" });

export const CommitmentCreate = z
  .strictObject({
    owner_membership_id: Uuid.describe("Who you ask (a person or a DE of the company)"),
    intent: text(500).describe("One sentence: what and why"),
    expected_outcome: text(2000).describe("What done looks like, checkably"),
    done_when: z.array(text(200)).max(10).optional(),
    request_type: z
      .string()
      .max(64)
      .regex(/^[a-z][a-z0-9_]{0,63}$/)
      .optional()
      .describe("Its type (operator data per type gives its default due); default `request`"),
    due_at: Timestamp.optional().describe(
      "When it is due. Absent: the type's default; neither: refused",
    ),
    accept_by: Timestamp.optional(),
    verify: CommitmentVerify.optional().describe("Default: requester (you confirm the result)"),
    verifier_membership_id: Uuid.optional().describe("A peer or human verifier, never the owner"),
    parent_id: Uuid.optional().describe("The commitment this one serves"),
    options: z.array(CommitmentOptionInput).min(2).max(4).optional().describe("A decision's"),
    default_option: z.string().max(1).optional(),
    blocker_category: BlockerCategory.optional(),
  })
  .meta({ id: "CommitmentCreate" });

export const CommitmentAction = z
  .strictObject({
    action: CommitmentMove,
    reason: text(500).optional().describe("reject, cancel"),
    note: text(500).optional().describe("progress, choose, forward, extend"),
    attempt: z.boolean().optional().describe("progress: one more self-fix attempt"),
    replan: z.boolean().optional().describe("progress: a different approach now"),
    result: text(4000).optional().describe("submit"),
    evidence: z.array(CommitmentEvidence).max(20).optional().describe("submit"),
    pass: z.boolean().optional().describe("verify"),
    feedback: text(1000).optional().describe("verify: what misses (when not passing)"),
    option: z.string().max(1).optional().describe("choose: the option's key (A..D)"),
    to_membership_id: Uuid.optional().describe("forward"),
    due_at: Timestamp.optional().describe("extend"),
    failure: z
      .strictObject({
        cause: CommitmentCause,
        category: BlockerCategory.nullable().optional(),
        what_happened: text(300),
        tried: z.array(text(200)).max(10),
        partial: z
          .array(z.strictObject({ label: text(160), ref: text(200).nullable().optional() }))
          .max(10)
          .optional(),
        options: z.array(CommitmentOptionInput).min(2).max(4),
        recommendation: text(300),
        question: text(200).optional(),
        default_option: z.string().max(1).optional(),
        decide_by: Timestamp.optional(),
      })
      .optional()
      .describe("fail: the failure report (2-4 options, one recommended)"),
  })
  .meta({ id: "CommitmentAction" });

const ERRORS = ["conflict"] as const;

export const commitmentRoutes = {
  "commitments.list": defineRoute({
    ...core,
    id: "commitments.list",
    method: "get",
    path: `${base}/commitments`,
    tag: "inbox",
    summary:
      "Your commitments: the requests asked of you (or escalated to you) and the ones you asked, with due times and states",
    access: "member",
    params: CompanyParams,
    query: CommitmentListQuery,
    response: { status: 200, schema: CommitmentList },
    errors: [],
    emits: [],
  }),
  "commitments.get": defineRoute({
    ...core,
    id: "commitments.get",
    method: "get",
    path: `${base}/commitments/{id}`,
    tag: "inbox",
    summary: "One commitment you are part of, with its log (who was told when)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: CommitmentView },
    errors: [],
    emits: [],
  }),
  "commitments.create": defineRoute({
    ...core,
    id: "commitments.create",
    method: "post",
    path: `${base}/commitments`,
    tag: "inbox",
    summary:
      "Ask a person or a DE for something: it gets an owner, a due time (yours, or the type's default; neither is refused), a verify step and an escalation ladder",
    access: "member",
    params: CompanyParams,
    body: CommitmentCreate,
    idempotency: "optional",
    response: { status: 201, schema: CommitmentView },
    errors: [...ERRORS],
    emits: ["commitment.created", "commitment.changed", "inbox_item.created", "wakeup.requested"],
  }),
  "commitments.act": defineRoute({
    ...core,
    id: "commitments.act",
    method: "post",
    path: `${base}/commitments/{id}/actions`,
    tag: "inbox",
    summary:
      "Move a commitment: accept or decline it, report progress, hand in a result, verify one (never your own), fail it with options, choose an option, forward, extend or withdraw it",
    access: "member",
    params: CompanyItemParams,
    body: CommitmentAction,
    response: { status: 200, schema: CommitmentView },
    errors: [...ERRORS],
    emits: ["commitment.changed", "commitment.created", "inbox_item.created", "wakeup.requested"],
  }),
};
